import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/features';
import {
  getChargePointsByGridConnectionIdSelector,
  getChargePointsSelector,
} from '@/features/ChargePoints';
import { selectAllConnectorsByGridConnectionId } from '@/features/Connectors';
import {
  ConnectorStatusEnum,
  StatusCb8Enum,
  type ChargePoint,
  type Connector,
  type ElectricityMeter,
  type GridConnection,
} from '@/types/swagger';
import _ from 'lodash';
import { shallowEqual } from 'react-redux';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface GridConnectionChargeMonitorProps {
  gridConnection: GridConnection;
  gridConnectionChargePoints: ChargePoint[];
  metersPower: string;
  chargersPower?: string | null;
  connectors: Connector[];
}

const GridConnectionChargeMonitor = ({
  gridConnection,
  gridConnectionChargePoints,
  metersPower,
  chargersPower,
  connectors,
}: GridConnectionChargeMonitorProps) => {
  const chargePoints = useAppSelector(
    (state) =>
      gridConnection
        ? getChargePointsByGridConnectionIdSelector(state, gridConnection.id)
        : getChargePointsSelector(state),
    shallowEqual
  );
  const allConnectorsByGridConnectionId = useAppSelector(selectAllConnectorsByGridConnectionId);
  const allGcConnectors = useMemo(
    () =>
      allConnectorsByGridConnectionId[gridConnection?.id]
        ? Object.values(allConnectorsByGridConnectionId[gridConnection.id]).filter(
            (connector) =>
              connector.connector_id !== 0 &&
              chargePoints[connector.charge_point]?.status !== StatusCb8Enum.Disabled
          )
        : [],
    [allConnectorsByGridConnectionId, chargePoints, gridConnection]
  );

  const offlineConnectors = useMemo(
    () =>
      allGcConnectors.filter(
        (connector) =>
          chargePoints[connector.charge_point]?.status !== StatusCb8Enum.Online &&
          chargePoints[connector.charge_point]?.status !== StatusCb8Enum.Disabled
      ),
    [allGcConnectors, chargePoints]
  );

  const [connectorCount, setConnectorCount] = useState({
    available: 0,
    preparing: 0,
    charging: 0,
    finishing: 0,
    unavailable: 0,
    faulted: 0,
    suspendedev: 0,
    suspendedevse: 0,
    reserved: 0,
    offline: 0,
  });
  const formattedMetersPower = useMemo(() => {
    const newMetersPower = parseInt(metersPower, 10) / 1000 || 0;
    return newMetersPower;
  }, [metersPower]);
  const formattedChargersPower = useMemo(() => {
    const newChargersPower = parseInt(chargersPower ?? '0', 10) / 1000 || 0;
    return newChargersPower;
  }, [chargersPower]);
  const totalChargersPower = useMemo(() => {
    if (gridConnection && gridConnectionChargePoints) {
      const reducer = (acc: number, current: ChargePoint) => acc + (current.capacity ?? 0);
      const totalChargersPower = gridConnectionChargePoints
        .filter(
          (chargerPoint) =>
            chargerPoint.status !== StatusCb8Enum.Waiting &&
            chargerPoint.status !== StatusCb8Enum.Disabled
        )
        .reduce(reducer, 0);
      return totalChargersPower;
    }
    return 0;
  }, [gridConnection, gridConnectionChargePoints]);
  const filteredConnectors = useMemo(
    () =>
      connectors?.filter?.(
        (connector) =>
          connector.connector_id !== 0 &&
          chargePoints?.[connector.charge_point]?.status === StatusCb8Enum.Online
      ) ?? [],
    [connectors, chargePoints]
  );

  useEffect(() => {
    const connectorsGroupedByStatus = _.groupBy(filteredConnectors, (connector) => {
      const statusNotification = connector.latest_status_notification;
      const status = connector.latest_status_notification
        ? statusNotification.status
        : ConnectorStatusEnum.Available;
      return status;
    });
    const available = connectorsGroupedByStatus[ConnectorStatusEnum.Available]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Available].length
      : 0;
    const preparing = connectorsGroupedByStatus[ConnectorStatusEnum.Preparing]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Preparing].length
      : 0;
    const charging = connectorsGroupedByStatus[ConnectorStatusEnum.Charging]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Charging].length
      : 0;
    const finishing = connectorsGroupedByStatus[ConnectorStatusEnum.Finishing]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Finishing].length
      : 0;
    const unavailable = connectorsGroupedByStatus[ConnectorStatusEnum.Unavailable]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Unavailable].length
      : 0;
    const faulted = connectorsGroupedByStatus[ConnectorStatusEnum.Faulted]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Faulted].length
      : 0;
    const suspendedev = connectorsGroupedByStatus[ConnectorStatusEnum.SuspendedEV]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.SuspendedEV].length
      : 0;
    const suspendedevse = connectorsGroupedByStatus[ConnectorStatusEnum.SuspendedEVSE]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.SuspendedEVSE].length
      : 0;
    const reserved = connectorsGroupedByStatus[ConnectorStatusEnum.Reserved]
      ? connectorsGroupedByStatus[ConnectorStatusEnum.Reserved].length
      : 0;
    const offline = offlineConnectors ? offlineConnectors.length : 0;
    const newConnectorCount = {
      available,
      preparing,
      charging,
      finishing,
      unavailable,
      faulted,
      suspendedev,
      suspendedevse,
      reserved,
      offline,
    };

    setConnectorCount((connectorCount) => {
      if (JSON.stringify(newConnectorCount) !== JSON.stringify(connectorCount))
        return newConnectorCount;
      return connectorCount;
    });
  }, [filteredConnectors, offlineConnectors]);

  const barChartData = [
    {
      name: 'Disponibles',
      available: connectorCount.available,
      charging: 0,
      preparing: 0,
      finishing: 0,
      suspendedev: 0,
      suspendedevse: 0,
      reserved: 0,
      faulted: 0,
      unavailable: 0,
      offline: 0,
    },
    {
      name: 'Ocupados',
      available: 0,
      charging: connectorCount.charging,
      preparing: connectorCount.preparing,
      finishing: connectorCount.finishing,
      suspendedev: connectorCount.suspendedev,
      suspendedevse: connectorCount.suspendedevse,
      reserved: connectorCount.reserved,
      faulted: 0,
      unavailable: 0,
      offline: 0,
    },
    {
      name: 'Con falla',
      available: 0,
      charging: 0,
      preparing: 0,
      finishing: 0,
      suspendedev: 0,
      suspendedevse: 0,
      reserved: 0,
      faulted: connectorCount.faulted,
      unavailable: connectorCount.unavailable,
      offline: 0,
    },
    {
      name: 'Fuera de línea',
      available: 0,
      charging: 0,
      preparing: 0,
      finishing: 0,
      suspendedev: 0,
      suspendedevse: 0,
      reserved: 0,
      faulted: 0,
      unavailable: 0,
      offline: connectorCount.offline,
    },
  ];

  const getGrayValue = (chargerPower: number, meterPower: number, total: number) => {
    if (chargerPower && !meterPower) {
      if (chargerPower >= total) return 0;
      return total - chargerPower;
    } else if (!chargerPower && meterPower) {
      if (meterPower >= total) return 0;
      return total - meterPower;
    } else if (chargerPower && meterPower) {
      if (chargerPower >= meterPower && chargerPower >= total) return 0;
      if (meterPower >= chargerPower && meterPower >= total) return 0;
      if (chargerPower >= meterPower && chargerPower < total) return total - chargerPower;
      if (meterPower >= chargerPower && meterPower < total) return total - meterPower;
    } else {
      if (total) return total;
      else return 1;
    }
  };

  const hadMeters = (meters: ElectricityMeter[]) => {
    if (meters?.length) {
      let meterOnline = false;
      meters.forEach((meter) => {
        if (meter.status === 'Activo') meterOnline = true;
      });
      return meterOnline;
    } else return false;
  };

  const pieChartData = [
    {
      name: 'formattedChargePower',
      value: formattedChargersPower,
      color: '#405fb9',
    },
    {
      name: 'idle',
      value:
        hadMeters(gridConnection.electricity_meters) &&
        formattedMetersPower > formattedChargersPower
          ? formattedMetersPower - formattedChargersPower
          : 0,
      color: '#26396f',
    },
    {
      name: 'gray',
      value: getGrayValue(
        formattedChargersPower,
        hadMeters(gridConnection.electricity_meters) ? formattedMetersPower : 0,
        gridConnection.capacity ? gridConnection.capacity / 1000 : totalChargersPower / 1000
      ),
      color: 'rgba(255, 255, 255, 0.3)',
    },
  ];

  const busyConnectors = useMemo(
    () =>
      connectorCount.charging +
      connectorCount.preparing +
      connectorCount.finishing +
      connectorCount.suspendedev +
      connectorCount.suspendedevse +
      connectorCount.reserved,
    [connectorCount]
  );

  const faultedConnectors = useMemo(
    () => connectorCount.faulted + connectorCount.unavailable,
    [connectorCount]
  );

  const CustomTooltip = ({ active, payload, label }: React.ComponentProps<typeof Tooltip>) => {
    if (!payload) return null;

    if (active) {
      switch (label) {
        case 'Disponibles':
          if (connectorCount.available === 0) return null;
          return (
            <div className="$tw-space-y-1 $tw-rounded-md $tw-bg-black/50 $tw-p-1">
              <div className="$tw-text-xs $tw-font-semibold $tw-text-foreground">{label}</div>
              <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                <div
                  className="$tw-size-3 $tw-border"
                  style={{ backgroundColor: 'rgba(55, 190, 110, 1)' }}
                />
                <span className="$tw-text-xs">{`Disponibles: ${payload[0].payload.available}`}</span>
              </div>
            </div>
          );
        case 'Ocupados':
          if (busyConnectors === 0) return null;
          return (
            <div className="$tw-space-y-1 $tw-rounded-md $tw-bg-black/50 $tw-p-1">
              <div className="$tw-text-xs $tw-font-semibold $tw-text-foreground">{label}</div>
              {payload[1].payload.charging > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(47, 100, 192, 1)' }}
                  />
                  <span className="$tw-text-xs">{`Cargando: ${payload[1].payload.charging}`}</span>
                </div>
              )}
              {payload[2].payload.preparing > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(47, 100, 192, 0.8)' }}
                  />
                  <span className="$tw-text-xs">{`Conectado: ${payload[2].payload.preparing}`}</span>
                </div>
              )}
              {payload[3].payload.finishing > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(47, 100, 192, 0.5)' }}
                  />
                  <span className="$tw-text-xs">{`Listo: ${payload[3].payload.finishing}`}</span>
                </div>
              )}
              {payload[4].payload.suspendedev > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(246, 38, 129, 0.2)' }}
                  />
                  <span className="$tw-text-xs">{`Vehículo no disponible: ${payload[4].payload.suspendedev}`}</span>
                </div>
              )}
              {payload[5].payload.suspendedevse > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(246, 38, 129, 0.4)' }}
                  />
                  <span className="$tw-text-xs">{`Cargador no disponible: ${payload[5].payload.suspendedevse}`}</span>
                </div>
              )}
              {payload[6].payload.reserved > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(55, 190, 110, 0.2)' }}
                  />
                  <span className="$tw-text-xs">{`Reservado: ${payload[6].payload.reserved}`}</span>
                </div>
              )}
            </div>
          );
        case 'Con falla':
          if (faultedConnectors === 0) return null;
          return (
            <div className="$tw-space-y-1 $tw-rounded-md $tw-bg-black/50 $tw-p-1">
              <div className="$tw-text-xs $tw-font-semibold $tw-text-foreground">{label}</div>
              {payload[7].payload.faulted > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(201, 45, 45, 1)' }}
                  />
                  <span className="$tw-text-xs">{`Falla de conector: ${payload[7].payload.faulted}`}</span>
                </div>
              )}
              {payload[8].payload.unavailable > 0 && (
                <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                  <div
                    className="$tw-size-3 $tw-border"
                    style={{ backgroundColor: 'rgba(201, 45, 45, 0.7)' }}
                  />
                  <span className="$tw-text-xs">{`Conector no disponible: ${payload[8].payload.unavailable}`}</span>
                </div>
              )}
            </div>
          );
        case 'Fuera de línea':
          if (connectorCount.offline === 0) return null;
          return (
            <div className="$tw-space-y-1 $tw-rounded-md $tw-bg-black/50 $tw-p-1">
              <div className="$tw-text-xs $tw-font-semibold $tw-text-foreground">{label}</div>
              <div className="$tw-inline-flex $tw-items-center $tw-gap-1">
                <div
                  className="$tw-size-3 $tw-border"
                  style={{ backgroundColor: 'rgba(180, 190, 190, 1)' }}
                />
                <span className="$tw-text-xs">{`Fuera de línea: ${payload[9].payload.offline}`}</span>
              </div>
            </div>
          );
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <div className="$tw-rounded-md $tw-border $tw-border-foreground/50 $tw-bg-foreground/5 $tw-p-4 $tw-text-foreground xl:$tw-px-10">
      <div className="$tw-grid $tw-grid-cols-1 $tw-items-center $tw-gap-4 md:$tw-grid-cols-3">
        <div className="$tw-flex $tw-flex-col $tw-items-center $tw-text-center md:$tw-items-start md:$tw-text-left">
          <h3 className="$tw-m-0 $tw-pb-2 $tw-text-2xl $tw-font-bold $tw-uppercase md:$tw-text-3xl">
            {gridConnection.name}
          </h3>
          <div className="$tw-flex $tw-flex-col $tw-justify-center $tw-text-center md:$tw-justify-start md:$tw-text-left">
            {gridConnection && gridConnectionChargePoints && (
              <div className="$tw-space-x-1">
                <span>
                  {
                    gridConnectionChargePoints.filter((cP) => cP.status !== StatusCb8Enum.Disabled)
                      .length
                  }
                </span>
                <span>
                  {gridConnectionChargePoints.filter((cP) => cP.status !== StatusCb8Enum.Disabled)
                    .length === 1
                    ? 'Cargador'
                    : 'Cargadores'}
                </span>
              </div>
            )}

            <div className="$tw-space-x-1">
              <span>{allGcConnectors.length}</span>
              <span>{allGcConnectors.length === 1 ? 'Conector' : 'Conectores'}</span>
              <span>({filteredConnectors.length} En línea)</span>
            </div>
          </div>
        </div>

        <div className="$tw-flex $tw-flex-col $tw-items-center $tw-justify-center md:$tw-justify-start xl:$tw-flex-row">
          <div className="gcm-power xl:$tw-mr-6">
            <div className="$tw-size-28">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 20 }}>
                  <Pie
                    data={pieChartData}
                    dataKey="value"
                    innerRadius={35}
                    outerRadius={55}
                    startAngle={225}
                    endAngle={-45}
                    stroke="none"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="$tw-flex $tw-w-full $tw-justify-between $tw-px-2 *:$tw-text-caption">
              <span>0 kW</span>
              <span>
                {gridConnection.capacity
                  ? Math.round(gridConnection.capacity / 1000)
                  : Math.round(totalChargersPower / 1000)}{' '}
                kW
              </span>
            </div>
          </div>
          <div className="$tw-flex $tw-w-fit $tw-flex-col $tw-gap-2">
            {hadMeters(gridConnection.electricity_meters) && (
              <div>
                <div className="$tw-flex $tw-items-center $tw-gap-1">
                  <div className="$tw-size-1.5 $tw-rounded-full $tw-bg-primary" />{' '}
                  <span className="$tw-text-xs $tw-text-muted-foreground">Demanda de empalme</span>
                </div>
                <span className="$tw-mr-1 $tw-text-caption $tw-font-semibold $tw-tracking-wide">
                  {Math.round(formattedMetersPower)}
                </span>
                <span className="$tw-mr-1 $tw-text-caption $tw-font-semibold $tw-tracking-wide">
                  kW
                </span>
              </div>
            )}
            <div>
              <div className="$tw-flex $tw-items-center $tw-gap-1">
                <div className="$tw-size-1.5 $tw-rounded-full $tw-bg-primary" />{' '}
                <span className="$tw-text-xs $tw-text-muted-foreground">Demanda de cargadores</span>
              </div>
              <span className="$tw-mr-1 $tw-text-caption $tw-font-semibold $tw-tracking-wide">
                {Math.round(formattedChargersPower)}
              </span>
              <span className="$tw-mr-1 $tw-text-caption $tw-font-semibold $tw-tracking-wide">
                kW
              </span>
              <span className="$tw-text-caption $tw-font-semibold $tw-tracking-wide">
                (máx. {Math.round(totalChargersPower / 1000)} kW)
              </span>
            </div>
          </div>
        </div>

        <div className="status-chart-container $tw-flex $tw-flex-col $tw-items-center $tw-justify-center">
          <h4 className="$tw-m-0 $tw-text-sm $tw-text-muted-foreground">Resumen de conectores</h4>
          <div className="$tw-flex $tw-w-full $tw-justify-center">
            <div className="$tw-w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barChartData}
                  layout="vertical"
                  barSize={10}
                  margin={{ left: 0, right: 0 }}
                >
                  <XAxis hide axisLine={false} type="number" domain={[0, allGcConnectors.length]} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    stroke="#fff"
                    fontSize={10}
                  />
                  <Tooltip cursor={{ fill: 'transparent' }} content={<CustomTooltip />} />
                  <Bar dataKey="available" fill="rgba(55, 190, 110, 1)" stackId="a" />
                  <Bar dataKey="charging" fill="rgba(47, 100, 192, 1)" stackId="a" />
                  <Bar dataKey="preparing" fill="rgba(47, 100, 192, 0.8)" stackId="a" />
                  <Bar dataKey="finishing" fill="rgba(47, 100, 192, 0.5)" stackId="a" />
                  <Bar dataKey="suspendedev" fill="rgba(246, 38, 129, 0.2)" stackId="a" />
                  <Bar dataKey="suspendedevse" fill="rgba(246, 38, 129, 0.4)" stackId="a" />
                  <Bar dataKey="reserved" fill="rgba(55, 190, 110, 0.2)" stackId="a" />
                  <Bar dataKey="faulted" fill="rgba(201, 45, 45, 1)" stackId="a" />
                  <Bar dataKey="unavailable" fill="rgba(201, 45, 45, 0.7)" stackId="a" />
                  <Bar dataKey="offline" fill="rgba(180, 190, 190, 1)" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="$tw-ml-2 $tw-flex $tw-h-full $tw-flex-col $tw-items-start $tw-justify-center *:$tw-text-nowrap *:$tw-text-sm">
              <span>
                {`${connectorCount.available + connectorCount.reserved} / ${allGcConnectors.length}`}
              </span>
              <span>
                {`${
                  connectorCount.charging +
                  connectorCount.finishing +
                  connectorCount.preparing +
                  connectorCount.suspendedev +
                  connectorCount.suspendedevse
                } / ${allGcConnectors.length}`}
              </span>
              <span>
                {`${connectorCount.faulted + connectorCount.unavailable} / ${allGcConnectors.length}`}
              </span>
              <span>{`${connectorCount.offline} / ${allGcConnectors.length}`}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridConnectionChargeMonitor;
